"use client";

import { useTranslations } from "next-intl";
import { useFormatter } from "@/hooks/use-formatter"
import {
  IconArrowBackUp,
  IconCalendarEvent,
  IconCheck,
  IconCircleCheck,
  IconCircleX,
  IconUserOff,
} from "@tabler/icons-react";

import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import type { CheckinStep } from "@/lib/appointment-workflow-api";
import { isTerminalStatus, statusTransitions } from "@/lib/appointment-status";
import type { Appointment, AppointmentStatus } from "@/types";

function isToday(iso: string): boolean {
  const d = new Date(iso);
  const now = new Date();
  return (
    d.getFullYear() === now.getFullYear() &&
    d.getMonth() === now.getMonth() &&
    d.getDate() === now.getDate()
  );
}

interface AppointmentSheetActionsProps {
  appointment: Appointment;
  pending: boolean;
  onStatus: (status: AppointmentStatus) => void;
  onCheckIn: (step: CheckinStep) => void;
}

/**
 * 86-01: the header actions row. Today's visits carry the day-of check-in
 * ladder (Arrived → In chair → Done, with Undo); every row carries the
 * outcomes from `statusTransitions` — three ways out of scheduled, one way
 * back from a terminal state.
 */
export function AppointmentSheetActions({
  appointment,
  pending,
  onStatus,
  onCheckIn,
}: AppointmentSheetActionsProps) {
  const t = useTranslations("appointments.sheet.checkin");
  const tq = useTranslations("appointmentQuickActions");
  const format = useFormatter();

  const isVisit = appointment.type == null || appointment.type === "clinic_visit";
  const terminal = isTerminalStatus(appointment.status);
  const showCheckIn = isVisit && isToday(appointment.scheduled_at) && (!terminal || !!appointment.time_dismissed);

  const stamp = (iso: string | null | undefined) =>
    iso ? format.dateTime(new Date(iso), { hour: "2-digit", minute: "2-digit" }) : null;

  const steps: Array<{ step: Exclude<CheckinStep, "undo">; label: string; at: string | null }> = [
    { step: "arrived", label: t("arrived"), at: stamp(appointment.time_arrived) },
    { step: "seated", label: t("seated"), at: stamp(appointment.time_seated) },
    { step: "dismissed", label: t("done"), at: stamp(appointment.time_dismissed) },
  ];
  const anyStamp = steps.some((s) => s.at);
  // The next open step is the one the desk is most likely to press.
  const nextIndex = steps.findIndex((s) => !s.at);

  const outcomes = statusTransitions(appointment.status);
  const outcomeIcon = (status: AppointmentStatus) => {
    switch (status) {
      case "completed":
        return <IconCircleCheck className="mr-1.5 size-4 text-primary" aria-hidden />;
      case "no_show":
        return <IconUserOff className="mr-1.5 size-4 text-muted-foreground" aria-hidden />;
      case "cancelled":
        return <IconCircleX className="mr-1.5 size-4 text-destructive" aria-hidden />;
      default:
        return <IconCalendarEvent className="mr-1.5 size-4 text-primary" aria-hidden />;
    }
  };
  const outcomeLabel = (status: AppointmentStatus) => {
    switch (status) {
      case "completed":
        return tq("completed");
      case "no_show":
        return tq("noShow");
      case "cancelled":
        return tq("cancelled");
      default:
        return tq("backToScheduled");
    }
  };

  return (
    <div className="mt-3 flex flex-col gap-2" data-slot="appointment-sheet-actions">
      {showCheckIn && (
        <div className="flex items-center gap-1.5" role="group" aria-label={t("label")}>
          {steps.map((s, i) => {
            const done = !!s.at;
            const isNext = i === nextIndex;
            return (
              <button
                key={s.step}
                type="button"
                disabled={pending || done}
                onClick={() => onCheckIn(s.step)}
                aria-pressed={done}
                className={cn(
                  "flex h-8 min-w-0 flex-1 items-center justify-center gap-1.5 rounded-[8px] border px-2 text-[12px] font-medium transition-colors",
                  done
                    ? "border-primary/40 bg-primary/10 text-foreground"
                    : isNext
                      ? "border-primary bg-primary text-primary-foreground hover:bg-primary/90"
                      : "bg-muted/40 text-muted-foreground hover:border-primary/50",
                  (pending || done) && "cursor-default"
                )}
              >
                {done && <IconCheck className="size-3.5 shrink-0" aria-hidden />}
                <span className="truncate">{s.label}</span>
                {s.at && <span className="tabular-nums text-[11px] text-muted-foreground">{s.at}</span>}
              </button>
            );
          })}
          {anyStamp && (
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="size-8 shrink-0"
              disabled={pending}
              onClick={() => onCheckIn("undo")}
              aria-label={t("undo")}
              title={t("undo")}
            >
              <IconArrowBackUp className="size-4" />
            </Button>
          )}
        </div>
      )}

      <div
        className={cn("grid gap-2", outcomes.length === 1 ? "grid-cols-1" : "grid-cols-3")}
        role="group"
        aria-label={tq("statusSection")}
      >
        {outcomes.map((status) => (
          <Button
            key={status}
            type="button"
            variant="outline"
            size="sm"
            disabled={pending}
            onClick={() => onStatus(status)}
            className="h-8"
          >
            {outcomeIcon(status)}
            {outcomeLabel(status)}
          </Button>
        ))}
      </div>
    </div>
  );
}
